import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

// Import API models and service
import {
  MaterialControllerService,
  MaterialDTO,
  MaterialListDTO,
  PageRequestDTO,
} from '@app/api/goods-service';

// Import local interfaces
import { MaterialItem } from '../material.config';

// API Response interfaces
export interface MaterialListResponse {
  data: MaterialItem[];
  totalRecords: number;
  loading: boolean;
}

export interface MaterialSearchParams {
  code?: string;
  materialName?: string;
  page?: number;
  size?: number;
}

// Image data interface for server communication
export interface MaterialImageData {
  previewUrl: string; // URL or binary data for server
  fileName: string;
  fileSize: number;
  file?: File; // Actual File object for upload
}

// Carrier for the multipart create12/update13 text + file fields (mainImage/
// detailImage hold raw File blobs here, unlike MaterialDTO's URL strings)
interface MaterialMultipartFields {
  materialName: string;
  code?: string;
  detailHtml?: string;
  remark?: string;
  mainImage?: File[];
  detailImage?: File[];
}

@Injectable({
  providedIn: 'root',
})
export class MaterialService {
  constructor(
    private readonly materialControllerService: MaterialControllerService,
  ) {}

  // Get materials with pagination and search
  getMaterials(
    page: number = 1,
    pageSize: number = 20,
    filters?: MaterialSearchParams,
  ): Observable<MaterialListResponse> {
    // Convert search params to API DTO
    const materialListDTO: MaterialListDTO = {
      code: filters?.code,
      materialName: filters?.materialName,
    };

    const pageRequest: PageRequestDTO = {
      page: page - 1, // API uses 0-based pagination
      size: pageSize,
    };

    return this.materialControllerService
      .getList1(
        filters?.code,
        filters?.materialName,
        undefined,
        undefined,
        pageRequest.page,
        pageRequest.size,
      )
      .pipe(
        map((response: any) => {
          // Handle blob response
          if (response instanceof Blob) {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const jsonResponse = JSON.parse(reader.result as string);
                  resolve(this.processApiResponse(jsonResponse));
                } catch (error) {
                  console.error('Failed to parse blob as JSON:', error);
                  reject(error);
                }
              };
              reader.onerror = () => reject(reader.error);
              reader.readAsText(response);
            });
          }

          // Handle regular JSON response
          return this.processApiResponse(response);
        }),
        // Handle the Promise from blob parsing
        switchMap((result: any) => {
          if (result instanceof Promise) {
            return from(result);
          }
          return of(result);
        }),
        catchError((error) => {
          console.error('Error fetching materials:', error);
          throw error;
        }),
      );
  }

  // Get material by ID
  getMaterialById(id: string): Observable<MaterialItem | null> {
    const numericId = parseInt(id);
    if (isNaN(numericId)) {
      return of(null);
    }

    return this.materialControllerService.detail3(numericId).pipe(
      map((response: any) => {
        // Handle blob response
        if (response instanceof Blob) {
          return new Promise<MaterialItem | null>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const jsonResponse = JSON.parse(reader.result as string);
                const body = jsonResponse?.data ?? jsonResponse;
                resolve(this.mapApiToComponentRecord(body));
              } catch (error) {
                console.error('Failed to parse blob as JSON:', error);
                reject(error);
              }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(response);
          });
        }

        // Unwrap { data } when API returns wrapped response
        const body = response?.data ?? response;
        const mappedResult = this.mapApiToComponentRecord(body);
        return mappedResult;
      }),
      // Handle the Promise from blob parsing
      switchMap((result: any) => {
        if (result instanceof Promise) {
          return from(result);
        }
        return of(result);
      }),
      catchError((error) => {
        console.error('Error fetching material detail:', error);
        return of(null);
      }),
    );
  }

  // Create new material
  createMaterial(
    materialData: Partial<MaterialItem>,
  ): Observable<MaterialItem> {
    // Convert component data to multipart fields
    const materialDTO = this.mapComponentToApiRecord(materialData);
    const isExternalVideo = materialData.usageVideoType === 'EXTERNAL_LINK';
    const usageVideoFile = isExternalVideo
      ? undefined
      : this.convertUsageVideoFile(materialData);
    return this.materialControllerService
      .create13(
        materialDTO.materialName,
        materialDTO.code,
        materialDTO.detailHtml,
        materialDTO.remark,
        materialData.usageVideoPublishStatus || undefined,
        materialData.usageVideoType || 'SELF_HOSTED',
        isExternalVideo ? materialData.usageVideoExternalUrl : undefined,
        materialDTO.mainImage,
        materialDTO.detailImage, // This will be sent as detailImages to the API
        usageVideoFile,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        map((response: any) => {
          if (!response) {
            throw new Error('No response received from server');
          }

          // Handle different possible response structures
          let materialData = response;

          // If response has a data property, use that
          if (response.data) {
            materialData = response.data;
          }

          // If response has a result property, use that
          if (response.result) {
            materialData = response.result;
          }

          return this.mapApiToComponentRecord(materialData);
        }),
        catchError((error) => {
          console.error('Error creating material:', error);
          throw error;
        }),
      );
  }

  // Update material
  updateMaterial(
    id: string,
    materialData: Partial<MaterialItem>,
  ): Observable<MaterialItem | null> {
    const numericId = parseInt(id);
    if (isNaN(numericId)) {
      throw new Error('Invalid material ID');
    }

    const materialName = materialData.materialName || '';
    const code = materialData.code || undefined;
    const detailHtml = materialData.detailHtml || undefined;
    const remark = materialData.remark || undefined;

    // Handle removed images
    const removeMainImages = materialData.removedMainImages || [];
    const removeDetailImages = materialData.removedDetailImages || [];

    // Only include new images if they exist (not null)
    const mainImage = materialData.mainImage
      ? this.convertImageToServerFormat(materialData.mainImage)
      : undefined;
    const detailImage =
      materialData.detailImages || materialData.detailImage
        ? this.convertDetailImagesToServerFormat(
            materialData.detailImages || materialData.detailImage,
          )
        : undefined;
    const isExternalVideo = materialData.usageVideoType === 'EXTERNAL_LINK';
    const usageVideoFile = isExternalVideo
      ? undefined
      : this.convertUsageVideoFile(materialData);

    return this.materialControllerService
      .update14(
        numericId,
        materialName,
        code,
        detailHtml,
        remark,
        materialData.usageVideoPublishStatus || undefined,
        materialData.usageVideoType || 'SELF_HOSTED',
        isExternalVideo ? materialData.usageVideoExternalUrl : undefined,
        removeMainImages.length > 0 ? removeMainImages : undefined,
        removeDetailImages.length > 0 ? removeDetailImages : undefined,
        materialData.removeUsageVideo || undefined,
        mainImage,
        detailImage,
        usageVideoFile,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        map((response: any) => {
          if (!response) {
            throw new Error('No response received from server');
          }

          let responseData = response;

          if (response.data) {
            responseData = response.data;
          }

          if (response.result) {
            responseData = response.result;
          }

          return this.mapApiToComponentRecord(responseData);
        }),
        catchError((error) => {
          console.error('Error updating material:', error);
          throw error;
        }),
      );
  }

  deleteMaterial(id: string): Observable<boolean> {
    const numericId = parseInt(id);
    if (isNaN(numericId)) {
      return of(false);
    }

    return this.materialControllerService._delete(numericId, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  batchDeleteMaterials(ids: string[]): Observable<any> {
    const numericIds = ids.map((id) => parseInt(id)).filter((id) => !isNaN(id));

    if (numericIds.length === 0) {
      return of(false);
    }

    const idsReqDTO = { ids: numericIds };

    // Important: do NOT force httpHeaderAccept to 'application/json' here.
    // Backend returns plain text (e.g. 'Operation successful'), and asking
    // for JSON makes Angular try to JSON-parse it and wrap it in
    // HttpErrorResponse ("Http failure during parsing") even for 200.
    return this.materialControllerService
      .deleteByIds5(idsReqDTO, 'body', false)
      .pipe(
        map((response: any) => {
          // Normalize successful responses from API.
          // Backend may return plain text like 'operation successful'
          // or an object wrapper; in all these cases we just propagate
          // the response so the component's success handler runs.
          return response;
        }),
        catchError((error) => {
          // Accept: */* makes the generated client pick responseType 'blob'
          // (see the comment above), so on error Angular parses the JSON
          // error body into a Blob instead of an object — error.error.message
          // would otherwise always be undefined. Read the Blob back to text
          // and re-parse it so the component gets the real error message.
          if (error?.error instanceof Blob) {
            return from(error.error.text() as Promise<string>).pipe(
              switchMap((text) => {
                let parsedMessage = text;
                try {
                  parsedMessage = JSON.parse(text)?.message ?? text;
                } catch {
                  // Not JSON — fall back to the raw text as-is.
                }
                error.error = { message: parsedMessage };
                throw error;
              }),
            );
          }
          throw error;
        }),
      );
  }

  private mapApiToComponentRecord(apiItem: any): MaterialItem {
    if (!apiItem) {
      throw new Error('Invalid API response: response is null or undefined');
    }

    // API may return mainImageUrls/detailImageUrls or mainImage/detailImage (MaterialVO has both)
    const mainImageUrls: string[] =
      (Array.isArray(apiItem.mainImageUrls) && apiItem.mainImageUrls.length > 0)
        ? apiItem.mainImageUrls
        : (Array.isArray(apiItem.mainImage) ? apiItem.mainImage : []);
    const detailImageUrls: string[] =
      (Array.isArray(apiItem.detailImageUrls) && apiItem.detailImageUrls.length > 0)
        ? apiItem.detailImageUrls
        : (Array.isArray(apiItem.detailImage) ? apiItem.detailImage : []);

    const result: any = {
      id: apiItem.id?.toString() || '',
      materialName:
        apiItem.materialName && typeof apiItem.materialName === 'string'
          ? apiItem.materialName
          : '',
      code:
        apiItem.code && typeof apiItem.code === 'string' ? apiItem.code : '',
      detailHtml:
        apiItem.detailHtml && typeof apiItem.detailHtml === 'string'
          ? apiItem.detailHtml
          : '',
      remark:
        apiItem.remark && typeof apiItem.remark === 'string'
          ? apiItem.remark
          : '',
      mainImage: this.processImageDataFromServer(
        mainImageUrls,
        mainImageUrls,
      ),
      detailImages: this.processDetailImagesFromServer(
        detailImageUrls,
        detailImageUrls,
      ),
      // Legacy support for single detail image
      detailImage: this.processImageDataFromServer(
        detailImageUrls,
        detailImageUrls,
      ),
      // Preserve original URLs for component to use (from either field set)
      mainImageUrls,
      detailImageUrls,
      createdName: apiItem.createdName || '',
      creationDate: apiItem.creationDate
        ? new Date(apiItem.creationDate).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
        : '',
      status: apiItem.status !== undefined ? apiItem.status : true,
      usageVideo: apiItem.usageVideoUrl
        ? {
            previewUrl: apiItem.usageVideoUrl,
            fileName: this.extractFileNameFromUrl(apiItem.usageVideoUrl),
            fileSize: apiItem.usageVideoSizeBytes || 0,
            isExisting: true,
            isVideo: true,
          }
        : null,
      usageVideoPublishStatus: apiItem.usageVideoPublishStatus,
      usageVideoProcessingStatus: apiItem.usageVideoProcessingStatus,
      usageVideoThumbnailUrl: apiItem.usageVideoThumbnailUrl,
      usageVideoUrl: apiItem.usageVideoUrl,
      usageVideoType: apiItem.usageVideoType ?? 'SELF_HOSTED',
      // The API has no usageVideoExternalUrl field on read; for EXTERNAL_LINK
      // videos the external URL is carried in usageVideoUrl itself.
      usageVideoExternalUrl:
        apiItem.usageVideoType === 'EXTERNAL_LINK'
          ? apiItem.usageVideoUrl
          : undefined,
      usageVideoExternalPlatform: apiItem.usageVideoExternalPlatform,
    };

    return result;
  }

  // Only send a new file blob when the user picked one; an existing
  // (server) video must not be re-uploaded.
  private convertUsageVideoFile(
    materialData: Partial<MaterialItem>,
  ): File | undefined {
    const video = materialData.usageVideo;
    return video?.file instanceof File && !video.isExisting
      ? video.file
      : undefined;
  }

  // Multipart create12/update13 take File blobs directly (not URL strings like
  // MaterialDTO), so this carrier uses File[] rather than the MaterialDTO shape.
  private mapComponentToApiRecord(
    materialData: Partial<MaterialItem>,
  ): MaterialMultipartFields {
    if (!materialData.materialName || materialData.materialName.trim() === '') {
      throw new Error('Material name is required and cannot be empty');
    }

    const dto: MaterialMultipartFields = {
      materialName: materialData.materialName.trim(), // Required field - ensure exact field name
      code: materialData.code || undefined,
      detailHtml: materialData.detailHtml || undefined,
      remark: materialData.remark || undefined,
      mainImage: this.convertImageToServerFormat(materialData.mainImage),
      detailImage: this.convertDetailImagesToServerFormat(
        materialData.detailImages || materialData.detailImage,
      ),
    };
    return dto;
  }

  // Helper method to process image data from server
  private processImageDataFromServer(
    imageData: any,
    imageUrls: string[] | undefined,
  ): MaterialImageData | undefined {
    // First check if we have URL data (preferred for display)
    if (imageUrls && Array.isArray(imageUrls) && imageUrls.length > 0) {
      const url = imageUrls[0];
      return {
        previewUrl: url,
        fileName: this.extractFileNameFromUrl(url),
        fileSize: 0, // Cannot estimate size for URLs
      };
    }

    // Then check if we have binary data or URLs
    if (imageData) {
      // If it's an array of URLs or binary data, convert to our format
      if (Array.isArray(imageData) && imageData.length > 0) {
        const imageUrl = imageData[0]; // Take the first image

        return {
          previewUrl: imageUrl,
          fileName: this.extractFileNameFromUrl(imageUrl),
          fileSize: 0, // Cannot estimate size for URLs
        };
      }

      // If it's already in our format, return as is
      if (imageData.previewUrl) {
        return imageData;
      }
    }

    return undefined;
  }

  // Helper method to process multiple detail images from server
  private processDetailImagesFromServer(
    imageData: any,
    imageUrls: string[] | undefined,
  ): Array<MaterialImageData> | undefined {
    const detailImages: MaterialImageData[] = [];

    // First check if we have URL data (preferred for display)
    if (imageUrls && imageUrls.length > 0) {
      imageUrls.forEach((imageUrl, index) => {
        if (imageUrl) {
          const imageItem = {
            previewUrl: imageUrl,
            fileName: this.extractFileNameFromUrl(imageUrl),
            fileSize: 0, // Cannot estimate size for URLs
          };
          detailImages.push(imageItem);
        }
      });
    }

    // If we have image data from server
    if (imageData) {
      if (Array.isArray(imageData)) {
        imageData.forEach((item) => {
          if (item?.previewUrl) {
            detailImages.push({
              previewUrl: item.previewUrl,
              fileName:
                item.fileName || this.extractFileNameFromUrl(item.previewUrl),
              fileSize: item.fileSize || 0,
            });
          }
        });
      } else if (imageData.previewUrl) {
        detailImages.push({
          previewUrl: imageData.previewUrl,
          fileName:
            imageData.fileName ||
            this.extractFileNameFromUrl(imageData.previewUrl),
          fileSize: imageData.fileSize || 0,
        });
      }
    }

    return detailImages.length > 0 ? detailImages : undefined;
  }

  // Helper method to convert image data to server format
  private convertImageToServerFormat(imageData: any): File[] | undefined {
    if (!imageData) return undefined;

    // If we have a File object directly
    if (imageData.file && imageData.file instanceof File) {
      return [imageData.file];
    }

    // If it's already a File object
    if (imageData instanceof File) {
      return [imageData];
    }

    // If it's an array of objects with file property
    if (Array.isArray(imageData) && imageData.length > 0) {
      const files: File[] = [];

      imageData.forEach((item, index) => {
        if (item instanceof File) {
          files.push(item);
        } else if (item && item.file && item.file instanceof File) {
          files.push(item.file);
        }
      });

      return files.length > 0 ? files : undefined;
    }

    return undefined;
  }

  // Helper method to convert detail images to server format (supports both arrays and single images)
  private convertDetailImagesToServerFormat(
    detailImageData: any,
  ): File[] | undefined {
    if (!detailImageData) return undefined;

    // If it's an array of detail images
    if (Array.isArray(detailImageData)) {
      const files: File[] = [];

      detailImageData.forEach((item) => {
        if (item?.file instanceof File) {
          files.push(item.file);
        } else if (item instanceof File) {
          files.push(item);
        }
      });

      return files.length > 0 ? files : undefined;
    }

    // If it's a single detail image object
    if (detailImageData.file && detailImageData.file instanceof File) {
      return [detailImageData.file];
    }

    // If it's already a File object
    if (detailImageData instanceof File) {
      return [detailImageData];
    }

    return undefined;
  }

  // Helper method to process API response
  private processApiResponse(response: any): MaterialListResponse {
    let pageContent = [];
    let totalElements = 0;

    if (response) {
      // Try different possible response structures
      if (response.pageContent && Array.isArray(response.pageContent)) {
        pageContent = response.pageContent;
        totalElements = response.totalElements || 0;
      } else if (response.content && Array.isArray(response.content)) {
        pageContent = response.content;
        totalElements = response.totalElements || response.total || 0;
      } else if (response.data && Array.isArray(response.data)) {
        pageContent = response.data;
        totalElements = response.totalElements || response.total || 0;
      } else if (Array.isArray(response)) {
        pageContent = response;
        totalElements = response.length;
      } else {
        console.warn('Unexpected response structure:', response);
      }
    }

    // Convert API response to component format
    const data: MaterialItem[] = pageContent.map((item: any) => {
      return this.mapApiToComponentRecord(item);
    });

    return {
      data,
      totalRecords: totalElements,
      loading: false,
    };
  }

  // Helper method to extract filename from URL
  private extractFileNameFromUrl(url: string): string {
    const lastSlash = url.lastIndexOf('/');
    if (lastSlash !== -1) {
      const fileName = url.substring(lastSlash + 1);
      // If the filename doesn't have an extension, add one
      if (fileName && !fileName.includes('.')) {
        const filenameWithExt = `${fileName}.jpg`;
        return filenameWithExt;
      }

      return fileName || 'image.jpg';
    }

    return 'image.jpg'; // Default filename if not found
  }
}
